<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Importação QGC</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems" />
                    </div>
                    <div class="button-group">
                        <v-btn v-if="$check('Creditors', 'POST')" color="primary" elevation="0" to="/lista-credores/novo">
                            Incluir
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="filter-card">
                    <v-card-title class="filter-card-title">Filtro</v-card-title>
                    <v-card-text>
                        <v-form @submit.prevent="search">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="filter.name" label="Arquivo" outlined dense hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-autocomplete v-model="filter.client_id" label="Caso" :items="clients" item-text="name"
                                        item-value="id" outlined dense hide-details clearable no-data-text="Nenhum caso encontrado" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select v-model="filter.status" label="Situação" :items="statusOptions" item-text="name"
                                        item-value="id" outlined dense hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="2" class="d-flex align-center">
                                    <v-btn type="submit" color="primary" elevation="0" block>Filtrar</v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="table-card">
                    <v-card-title class="table-card-title">Importações</v-card-title>
                    <v-card-text>
                        <v-data-table class="app-table hover-table" :headers="headers" :items="items" :loading="loading"
                            :server-items-length="count" :options.sync="options" :footer-props="footerOptions"
                            :disable-sort="true" no-data-text="Nenhuma importação encontrada"
                            loading-text="Carregando dados..." @click:row="viewItem">

                            <template v-slot:[`item.filename`]="{ item }">
                                <span class="d-inline-flex align-center">
                                    <v-icon small class="mr-2" color="muted">mdi-file-delimited-outline</v-icon>{{ item.filename }}
                                </span>
                            </template>

                            <template v-slot:[`item.client`]="{ item }">
                                {{ item.client ? item.client.name : '—' }}
                            </template>

                            <template v-slot:[`item.version`]="{ item }">
                                {{ item.version | enum(versions, 'id', 'label') }}
                            </template>

                            <template v-slot:[`item.created_at`]="{ item }">
                                {{ item.created_at | defaultDate('DD/MM/YYYY HH:mm') }}
                            </template>

                            <template v-slot:[`item.status`]="{ item }">
                                <v-chip small label text-color="white" :color="statusColor(item.status)">
                                    {{ statusName(item.status) }}
                                </v-chip>
                            </template>

                            <template v-slot:[`item.actions`]="{ item }">
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <v-icon v-on="on" size="20" @click.stop="viewItem(item)">mdi-eye-outline</v-icon>
                                    </template>
                                    <span>Ver detalhes</span>
                                </v-tooltip>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios'
import qgcVersions from '@/common/data/qgc-versions.data.js'

// Situação da importação (API: CreditorImportService IMPORT_STATUS)
const STATUS = [
    { id: 1, name: 'Aguardando processamento', color: 'warning' },
    { id: 2, name: 'Processando', color: 'info' },
    { id: 3, name: 'Concluída', color: 'success' },
    { id: 4, name: 'Com erros', color: 'error' },
]

export default {
    name: "CreditorList",

    data: () => ({
        loading: false,
        items: [],
        count: 0,
        clients: [],
        versions: qgcVersions,
        statusOptions: STATUS,
        filter: { name: '', client_id: null, status: null },
        headers: [
            { text: "Arquivo", value: "filename" },
            { text: "Caso", value: "client" },
            { text: "Versão", value: "version" },
            { text: "Linhas", value: "total_rows", align: 'end' },
            { text: "Enviado em", value: "created_at" },
            { text: "Situação", value: "status" },
            { text: "", value: "actions", sortable: false, align: 'end' },
        ],
        options: { page: 1, itemsPerPage: 10 },
        footerOptions: {
            itemsPerPageText: 'Itens por página:',
            itemsPerPageOptions: [10, 20, 30, 40, 50],
            pageText: '{0}-{1} de {2}',
        },
    }),

    computed: {
        breadcrumbItems() {
            return [
                { text: "Início", to: "/home", exact: true },
                { text: "Importação QGC", disabled: true },
            ];
        },
    },

    watch: {
        options: {
            handler() { this.load(); },
            deep: true,
        },
    },

    created() {
        this.loadClients();
    },

    methods: {
        statusName(status) {
            const found = STATUS.find((item) => item.id === status)
            return found ? found.name : '—'
        },

        statusColor(status) {
            const found = STATUS.find((item) => item.id === status)
            return found ? found.color : 'grey'
        },

        async loadClients() {
            try {
                const result = await axios.get('/api/clients', { params: { page: 1, limit: 1000 } })
                this.clients = result.data.content.data
            } catch (error) {
                this.clients = []
            }
        },

        search() {
            if (this.options.page !== 1) this.options = { ...this.options, page: 1 }
            else this.load()
        },

        async load() {
            this.loading = true

            try {
                const search = Object.fromEntries(Object.entries(this.filter).filter(([, value]) => value))
                const result = await axios.get('/api/admin/creditor-claims/imports', {
                    params: { page: this.options.page, limit: this.options.itemsPerPage, search: JSON.stringify(search) },
                })

                this.items = result.data.content.data || []
                this.count = Number(result.data.content.total || 0)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao carregar as importações', { position: 'top-right', timeout: 5000 })
            } finally {
                this.loading = false
            }
        },

        viewItem(item) {
            this.$router.push(`/lista-credores/${item.id}`)
        },
    },
};
</script>
